import { useTranslation } from 'react-i18next';

/**
 * Enterprise-grade hook for ProductsTable translations with type safety and error handling
 * Provides centralized access to all ProductsTable-related translations
 */
export function useProductstableTranslations() {
  // Use the default translation namespace since productstable.json is merged into translation
  const { t } = useTranslation('translation');

  // Helper functions for common translation patterns with type safety
  const getPaginationText = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productstable.pagination.${key}`, params || {}));
  };

  const getToolbarText = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productstable.toolbar.${key}`, params || {}));
  };

  const getStateText = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productstable.states.${key}`, params || {}));
  };

  const getCategoryText = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productstable.categories.${key}`, params || {}));
  };

  const getErrorText = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productstable.errors.${key}`, params || {}));
  };

  const getExcelText = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productstable.excel.${key}`, params || {}));
  };

  const getConfirmationText = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productstable.confirmations.${key}`, params || {}));
  };

  const getSuccessText = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productstable.success.${key}`, params || {}));
  };

  const getExcelHeaderText = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productstable.excel.headers.${key}`, params || {}));
  };

  const getExcelStatusText = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productstable.excel.statusValues.${key}`, params || {}));
  };

  const getExcelWarningText = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productstable.excel.warnings.${key}`, params || {}));
  };

  const getExcelErrorText = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productstable.excel.errors.${key}`, params || {}));
  };

  // i18n Step 2: Adding new helper functions for constants
  const getConstantText = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productstable.constants.${key}`, params || {}));
  };

  const getViewModeText = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productstable.constants.viewModes.${key}`, params || {}));
  };

  const getFilterValueText = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productstable.constants.filterValues.${key}`, params || {}));
  };

  const getStorageText = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productstable.constants.storage.${key}`, params || {}));
  };

  const getQueryKeyText = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productstable.constants.queryKeys.${key}`, params || {}));
  };

  // i18n Step 2: Adding new helper functions for columns
  const getColumnHeaderText = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productstable.columns.headers.${key}`, params || {}));
  };

  const getColumnPlaceholderText = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productstable.columns.placeholders.${key}`, params || {}));
  };

  const getColumnTooltipText = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productstable.columns.tooltips.${key}`, params || {}));
  };

  const getColumnLabelText = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productstable.columns.labels.${key}`, params || {}));
  };

  const getMessageText = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productstable.messages.${key}`, params || {}));
  };

  // i18n Step 2: Adding new helper functions for filters
  const getFiltersText = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productstable.filters.${key}`, params || {}));
  };

  const getFiltersButtonText = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productstable.filters.buttons.${key}`, params || {}));
  };

  const getFiltersLabelText = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productstable.filters.labels.${key}`, params || {}));
  };

  const getFiltersPlaceholderText = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productstable.filters.placeholders.${key}`, params || {}));
  };

  const getFiltersOptionText = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productstable.filters.options.${key}`, params || {}));
  };

  const getFiltersMessageText = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productstable.filters.messages.${key}`, params || {}));
  };

  const getFiltersDateLabelText = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productstable.filters.dateLabels.${key}`, params || {}));
  };

  const getGridText = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productstable.grid.${key}`, params || {}));
  };

  const getFiltersPriceFormattingText = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productstable.filters.priceFormatting.${key}`, params || {}));
  };

  const getColumnVisibilityText = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productstable.columnVisibility.${key}`, params || {}));
  };

  const getBulkTagModalText = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productstable.bulkTagModal.${key}`, params || {}));
  };

  const getCategoryModalText = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productstable.categoryModal.${key}`, params || {}));
  };

  const getCategoryManagementText = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productstable.categoryModal.management.${key}`, params || {}));
  };

  const getBulkCategoryModalText = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productstable.bulkCategoryModal.${key}`, params || {}));
  };

  const getViewToggleText = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productstable.viewToggle.${key}`, params || {}));
  };

  // Safe translation wrapper that always returns string
  const tSafe = (key: string, params?: Record<string, unknown>): string => {
    return String(t(key, params || {}));
  };

  // Helper for product count with pluralization
  const getProductsCountText = (count: number, total: number): string => {
    const pluralizeKey = count === 1 ? 'productSingular' : 'productPlural';
    const pluralizedText = getPaginationText(pluralizeKey);
    return getPaginationText('productsCount', {
      count: count.toLocaleString(),
      total: total.toLocaleString(),
      pluralize: pluralizedText,
    });
  };

  // Helper for page information
  const getPageInfo = (pageIndex: number, pageSize: number, totalPages: number): string => {
    return `${getPaginationText('page')} ${pageIndex + 1} ${getPaginationText('of')} ${totalPages}`;
  };

  return {
    // Direct translation function with type safety
    t: tSafe,

    // Category-specific helpers
    getPaginationText,
    getToolbarText,
    getStateText,
    getCategoryText,
    getErrorText,
    getExcelText,
    getConfirmationText,
    getSuccessText,
    getExcelHeaderText,
    getExcelStatusText,
    getExcelWarningText,
    getExcelErrorText,

    // Combined helpers
    getProductsCountText,
    getPageInfo,

    // i18n Step 2: Constants helpers
    getConstantText,
    getViewModeText,
    getFilterValueText,
    getStorageText,
    getQueryKeyText,

    // i18n Step 2: Columns helpers
    getColumnHeaderText,
    getColumnPlaceholderText,
    getColumnTooltipText,
    getColumnLabelText,
    getMessageText,

    // i18n Step 2: Filters helpers
    getFiltersText,
    getFiltersButtonText,
    getFiltersLabelText,
    getFiltersPlaceholderText,
    getFiltersOptionText,
    getFiltersMessageText,
    getFiltersDateLabelText,
    getGridText,
    getFiltersPriceFormattingText,
    getColumnVisibilityText,
    getBulkTagModalText,
    getCategoryModalText,
    getCategoryManagementText,
    getBulkCategoryModalText,
    getBulkTagModalText,
    getViewToggleText,
  };
}

export type ProductstableTranslations = ReturnType<typeof useProductstableTranslations>;
